// src/pages/container_rental/components/SimpleReasonDialog.tsx
//
// Reused for skip and cancel — both have the same payload shape (`reason?`).

import { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  skipReasonSchema,
  SkipReasonValues,
} from "../../../contracts/container_rental/schemas.ts";

interface SimpleReasonDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmText?: string;
  confirmColor?: "primary" | "warning" | "error";
  onClose: () => void;
  onSubmit: (values: SkipReasonValues) => Promise<void> | void;
  loading?: boolean;
}

export default function SimpleReasonDialog({
  open,
  title,
  description,
  confirmText = "확인",
  confirmColor = "primary",
  onClose,
  onSubmit,
  loading = false,
}: SimpleReasonDialogProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SkipReasonValues>({
    resolver: zodResolver(skipReasonSchema),
    defaultValues: { reason: "" },
  });

  useEffect(() => {
    if (open) reset({ reason: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = handleSubmit(async (values) => {
    await onSubmit(values);
  });

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="simple-reason-title"
    >
      <DialogTitle id="simple-reason-title">{title}</DialogTitle>
      <DialogContent dividers>
        {description && (
          <div style={{ marginBottom: 12, color: "rgba(0,0,0,0.6)", fontSize: "0.875rem" }}>
            {description}
          </div>
        )}
        <Controller
          name="reason"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="사유 (선택)"
              multiline
              minRows={3}
              fullWidth
              error={Boolean(errors.reason)}
              helperText={errors.reason?.message}
              autoFocus
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          취소
        </Button>
        <Button
          onClick={submit}
          variant="contained"
          color={confirmColor}
          disabled={loading}
        >
          {loading ? "처리 중..." : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
